import React, { useEffect, useState } from "react";
import { Card } from "primereact/card";
import EventoContribuicaoFormController from "../controller/evento-contribuicao-form.controller";
import LerMais from "../../../../components/ler-mais/LerMais";
import { InputTextarea } from "primereact/inputtextarea";
import AnttButton from "../../../../../vendors/components/Button";
import {
  faCheck,
  faQuestion,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import FileUpload from "../../../../components/file-upload";
import If from "../../../../../vendors/components/If";
import EditorSigilo from "../../../../components/editor-sigilo";
import classNames from "classnames";
import { Checkbox } from "primereact/checkbox";
import AnexoContribuicao from "./AnexoContribuicao";
import InnerHtml from "../../../../components/inner-html";
import FilaContribuicaoUtil from "../../../../shared/utils/FilaContribuicaoUtil";
import ModalContribuirComoEvento from "./ModalContribuirComo";
import ModalCienciaEvento from "./ModalCienciaEvento";
import ModalPesquisaSatisfacao from "./ModalPesquisaSatisfacao";
import EventoFinalizarController from "../../../../components/evento-finalizar/evento-finalizar.controller";
import AjudaSigilo from "../../../../components/ajuda-sigilo";
import AjudaUpload from "../../../../components/ajuda-upload";
import ConfirmacaoSalvar from "../../../../components/confirmacao-salvar";
import OverlaySalvamentoContribuicao from "./OverlaySalvamentoContribuicao";
import useSalvamentoContribuicao from "../hooks/useSalvamentoContribuicao";

const MAX_CARACTERES = 20000;

function stripHtml(html: string) {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
}

interface Props {
  rotaVisualizar: string;
  painelJustificativaExibeRedacao?: boolean;
}

export default function ContribuicaoForm({
  rotaVisualizar,
  painelJustificativaExibeRedacao = false,
}: Props) {
  const controller = new EventoContribuicaoFormController();
  const eventoFinalizadorController = new EventoFinalizarController();
  const dispositivo = controller.dispostivo;
  const contribuicao = controller.contribuicao;
  const [showAjuda, setShowAjuda] = useState(false);
  const [showAjudaUpload, setShowAjudaUpload] = useState(false);
  const [showSalvar, setShowSalvar] = useState(false);
  const [fila, setFila] = useState(FilaContribuicaoUtil.getFila());
  const user = controller.getUser();
  const sigiloso = controller.sigiloso;
  const [dsContribuicao, setDsContribuicao] = useState("");
  const [dsContribuicaoSigilosa, setDsContribuicaoSigilosa] = useState("");
  const [dsJustificativa, setDsJustificativa] = useState("");

  const temAnexo = controller.arquivoList.length > 0;
  const temExclusaoAnexo =
    Array.isArray(controller.anexosExcluir) &&
    controller.anexosExcluir.length > 0;
  const temAlteracaoAnexo = temAnexo || temExclusaoAnexo;
  const {
    salvando,
    etapaAtual,
    sucesso,
    protocoloSucesso,
    etapasSalvamento,
    isEdit,
    exibirAvisoEmail,
    handleConfirmSalvar,
    handleSalvarEdicao,
    handleFecharOverlay,
  } = useSalvamentoContribuicao({
    controller,
    eventoFinalizadorController,
    temAnexo,
    temAlteracaoAnexo,
  });

  const dispositivoIndex = fila.findIndex(
    (id: any) => id == controller.getId()
  );
  const dispositivoAtual = dispositivoIndex >= 0 ? dispositivoIndex + 1 : 1;

  const redacaoPreenchida = sigiloso
    ? !!dsContribuicaoSigilosa?.trim()
    : !!dsContribuicao?.trim();
  const justificativaPreenchida = !!dsJustificativa?.trim();

  const redacaoCount = sigiloso
    ? stripHtml(dsContribuicaoSigilosa).length
    : dsContribuicao?.length ?? 0;
  const justificativaCount = dsJustificativa?.length ?? 0;

  const redacaoExcedida = redacaoCount > MAX_CARACTERES;
  const justificativaExcedida = justificativaCount > MAX_CARACTERES;

  const camposValidos =
    redacaoPreenchida &&
    justificativaPreenchida &&
    !redacaoExcedida &&
    !justificativaExcedida;

  const contribuiu =
    !!user &&
    !!user.contribuinte &&
    user.contribuinte.codContribuinte == contribuicao.codContribuinte;

  const ocultarDialogo =
    localStorage.getItem("ocultarDialogoContribuicao") === "true";

  const SalvarContribuicao = () => {
    if (dispositivo.stExibirDialogoContribuicao && !ocultarDialogo) {
      setShowSalvar(true);
    }
    if (!dispositivo.stExibirDialogoContribuicao || ocultarDialogo) {
      handleConfirmSalvar(false);
    }
  };

  const confirmarSalvar = (noMoreMessage: boolean) => {
    setShowSalvar(false);
    handleConfirmSalvar(noMoreMessage);
  };

  useEffect(() => {
    controller.init();
    eventoFinalizadorController.init();
    const state = controller.location.state;
    if (state && state.id && state.codAudienciaPublica) {
      eventoFinalizadorController.checkInitialModals(
        state.id,
        state.codAudienciaPublica
      );
    }
  }, []);

  useEffect(() => {
    contribuicao.setDsContribuicao("");
    contribuicao.setDsJustificativa("");
    contribuicao.setDsContribuicaoSigilosa("");
    controller.setArquivoList([]);
    controller.setSigiloso(false);

    controller.init();
    eventoFinalizadorController.init();

    setFila(FilaContribuicaoUtil.getFila());
    setShowSalvar(false);
    setShowAjuda(false);
    setShowAjudaUpload(false);
    setDsContribuicao("");
    setDsContribuicaoSigilosa("");
    setDsJustificativa("");

    eventoFinalizadorController.onHideContribuirComo();
    eventoFinalizadorController.onHideCiencia();
    eventoFinalizadorController.onHideSatisfacao();
    eventoFinalizadorController.setIsPrimeiraContrib(false);
    eventoFinalizadorController.setSatisfacaoResposta(null);
  }, [controller.getId()]);

  useEffect(() => {
    if (controller.isEdit) {
      setDsContribuicao(contribuicao.dsContribuicao || "");
      setDsContribuicaoSigilosa(contribuicao.dsContribuicaoSigilosa || "");
      setDsJustificativa(contribuicao.dsJustificativa || "");
    }
  }, [
    controller.isEdit,
    contribuicao.dsContribuicao,
    contribuicao.dsContribuicaoSigilosa,
    contribuicao.dsJustificativa,
  ]);

  return (
    <>
      <Card>
        <If condition={fila.length > 0}>
          <div
            style={{
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <i
              className="pi pi-exclamation-triangle"
              style={{ color: "#e6a817", fontSize: "1.5rem" }}
            />
            <b className="titulo-institucional">
              Contribuindo em vários dispositivos: {fila.length - 1} itens
              restantes
            </b>
          </div>
        </If>

        <div className="evento-contribuicao-wrapper">
          <If condition={contribuiu}>
            <div style={{ marginBottom: "12px" }}>
              <label>
                <b>Protocolo:</b> {contribuicao.protocolo}
              </label>
            </div>
          </If>

          <div className="evento-contribuicao-grid">
            <div className="ecf-panel">
              <div className="ecf-panel__header ecf-panel__header--simple">
                <h2 className="ecf-panel__title">Redação atual</h2>
              </div>
              <LerMais
                labelClassName="text-justify"
                maxLength={200}
                value={dispositivo.dsDispositivo}
              />
            </div>

            <div className="ecf-panel">
              <div className="ecf-panel__header">
                <h2 className="ecf-panel__title">Redação proposta</h2>
                <AnttButton
                  faIcon={faQuestion}
                  onClick={() => setShowAjuda(true)}
                  color="var(--azul-primario-claro)"
                  className="p-button-white-rounded"
                  tooltip="Ajuda"
                  tooltipOptions={{ position: "top" }}
                />
              </div>

              <If condition={!sigiloso}>
                <If condition={!controller.isView}>
                  <InputTextarea
                    cols={20}
                    value={contribuicao.dsContribuicao}
                    onChange={(e) => {
                      contribuicao.setDsContribuicao(e.target.value);
                      setDsContribuicao(e.target.value);
                    }}
                    rows={12}
                    className={classNames("ecf-textarea--edit", {
                      "p-invalid": !redacaoPreenchida,
                    })}
                  />
                </If>
                <If condition={controller.isView}>
                  <div className="ecf-content-box ecf-content-box--proposed">
                    <If
                      condition={
                        dispositivo.dsDispositivo == "ContribuicaoOutrosMeios"
                      }
                    >
                      <InnerHtml
                        value={
                          contribuicao.dsContribuicao == null
                            ? "contribuições por outros meios"
                            : contribuicao.dsContribuicao
                        }
                      />
                    </If>
                    <If
                      condition={
                        dispositivo.dsDispositivo != "ContribuicaoOutrosMeios"
                      }
                    >
                      <InnerHtml value={contribuicao.dsContribuicao} />
                    </If>
                  </div>
                </If>
              </If>

              <If condition={sigiloso}>
                <div
                  className="like-card"
                  style={{ minHeight: "280px", borderRadius: "4px" }}
                >
                  <EditorSigilo
                    value={contribuicao.dsContribuicaoSigilosa}
                    onChange={(e: any) => {
                      contribuicao.setDsContribuicaoSigilosa(e);
                      setDsContribuicaoSigilosa(e);
                    }}
                    readonly={controller.isView}
                    style={{ color: "black", height: "220px" }}
                  />
                </div>
              </If>

              {!controller.isView && (
                <div
                  className={classNames("ecf-char-counter", {
                    "ecf-char-counter--error": redacaoExcedida,
                  })}
                  style={{
                    fontSize: "0.8rem",
                    color: redacaoExcedida ? "#dc2626" : "#64748b",
                    fontWeight: redacaoExcedida ? 600 : 400,
                    textAlign: "right",
                    marginTop: "4px",
                    width: "100%",
                  }}
                >
                  {redacaoCount.toLocaleString("pt-BR")} de{" "}
                  {MAX_CARACTERES.toLocaleString("pt-BR")} caracteres utilizados
                </div>
              )}

              <div className="ecf-sigilo-action">
                <AnttButton
                  disabled={controller.isView}
                  className={classNames("ecf-sigilo-btn", {
                    "p-button-secondary": !sigiloso,
                  })}
                  onClick={() => controller.changeSigiloso()}
                  label="Contribuição Sigilosa"
                />
              </div>
            </div>

            <div className="ecf-panel">
              <div className="ecf-panel__header ecf-panel__header--simple">
                <h2 className="ecf-panel__title">Justificativa</h2>
              </div>
              <If condition={!controller.isView}>
                <InputTextarea
                  className={classNames(
                    "ecf-textarea--readonly ecf-textarea--short",
                    { "p-invalid": !justificativaPreenchida }
                  )}
                  cols={20}
                  readOnly={controller.isView}
                  value={contribuicao.dsJustificativa}
                  onChange={(e) => {
                    contribuicao.setDsJustificativa(e.target.value);
                    setDsJustificativa(e.target.value);
                  }}
                  rows={8}
                />
                <div
                  className={classNames("ecf-char-counter", {
                    "ecf-char-counter--error": justificativaExcedida,
                  })}
                  style={{
                    fontSize: "0.8rem",
                    color: justificativaExcedida ? "#dc2626" : "#64748b",
                    fontWeight: justificativaExcedida ? 600 : 400,
                    textAlign: "right",
                    marginTop: "4px",
                    width: "100%",
                  }}
                >
                  {justificativaCount.toLocaleString("pt-BR")} de{" "}
                  {MAX_CARACTERES.toLocaleString("pt-BR")} caracteres utilizados
                </div>
              </If>
              <If condition={controller.isView}>
                <div className="ecf-content-box ecf-textarea--short">
                  {painelJustificativaExibeRedacao ? (
                    <>
                      <If
                        condition={
                          dispositivo.dsDispositivo == "ContribuicaoOutrosMeios"
                        }
                      >
                        <InnerHtml
                          value={
                            contribuicao.dsContribuicao == null
                              ? "contribuições por outros meios"
                              : contribuicao.dsContribuicao
                          }
                        />
                      </If>
                      <If
                        condition={
                          dispositivo.dsDispositivo != "ContribuicaoOutrosMeios"
                        }
                      >
                        <InnerHtml value={contribuicao.dsContribuicao} />
                      </If>
                    </>
                  ) : (
                    <InnerHtml value={contribuicao.dsJustificativa} />
                  )}
                </div>
              </If>
            </div>

            <div
              className={classNames("ecf-panel", {
                "ecf-panel--hidden": !dispositivo.stAnexarArquivo,
              })}
            >
              <div className="ecf-panel__header">
                <h2 className="ecf-panel__title">Documentação</h2>
                <AnttButton
                  faIcon={faQuestion}
                  onClick={() => setShowAjudaUpload(true)}
                  color="var(--azul-primario-claro)"
                  className="p-button-white-rounded"
                  tooltip="Ajuda"
                  tooltipOptions={{ position: "top" }}
                />
              </div>

              <If condition={controller.isView}>
                <AnexoContribuicao
                  style={{
                    visibility: dispositivo.stAnexarArquivo
                      ? "visible"
                      : "hidden",
                  }}
                  controller={controller}
                  anexoOutrosMeios={
                    dispositivo.dsDispositivo == "ContribuicaoOutrosMeios"
                      ? true
                      : false
                  }
                />
              </If>

              <If condition={!controller.isView}>
                <div className="contribuicao-file">
                  <If
                    condition={
                      controller.isEdit &&
                      !!controller.lista &&
                      controller.lista.length > 0
                    }
                  >
                    {controller.lista.map((m: any, index: any) => (
                      <div key={"anexo-salvo-" + index} className="arquivo-item">
                        <div className="arquivo-item-main">
                          <b className="arquivo-item-name" title={m.noAnexo}>
                            {m.noAnexo}
                          </b>
                        </div>
                        <AnttButton
                          className="p-button-rounded arquivo-item-remove"
                          faIcon={faTrash}
                          onClick={() => controller.removerAnexoSalvo(m)}
                        />
                      </div>
                    ))}
                  </If>

                  {controller.arquivoList.map((m: any, index: any) => (
                    <div key={"arq" + index} className="arquivo-item">
                      <div className="arquivo-item-main">
                        <b className="arquivo-item-name" title={m.name}>
                          {m.name}
                        </b>
                        <If condition={sigiloso}>
                          <div className="arquivo-item-sigilo">
                            <Checkbox
                              checked={m.sigiloso}
                              onChange={() =>
                                controller.changeArquivoSigiloso(
                                  m,
                                  index,
                                  m.sigiloso
                                )
                              }
                            />
                            <label className="ml-2">Marcar como sigiloso</label>
                          </div>
                        </If>
                      </div>
                      <AnttButton
                        className="p-button-rounded arquivo-item-remove"
                        faIcon={faTrash}
                        onClick={() => controller.remove(m)}
                      />
                    </div>
                  ))}

                  <FileUpload
                    id="contribuicao-file-upload"
                    onUpload={(e: any) => controller.handleUpload(e)}
                    accept="*"
                    show={dispositivo.stAnexarArquivo}
                  />
                </div>
              </If>
            </div>
          </div>
        </div>

        <div className="ecf-actions">
          <If condition={controller.isView}>
            <AnttButton
              className="p-button-info mr-5"
              label="Voltar"
              onClick={() => {
                controller.history.push(controller.location.state.rollBackUrl, {
                  id: controller.location.state.idEvento,
                });
              }}
            />
          </If>
          <If condition={!controller.isView}>
            <AnttButton
              className="p-button-danger mr-5"
              label="&times; Cancelar"
              disabled={salvando}
              onClick={() => {
                if (isEdit) {
                  controller.history.push(
                    controller.location.state.rollBackUrl,
                    { id: controller.location.state.idEvento }
                  );
                } else {
                  FilaContribuicaoUtil.goToNext(
                    controller.history,
                    controller.getId(),
                    controller.codAudienciaPublica
                  );
                }
              }}
            />
            <AnttButton
              faIcon={faCheck}
              color="white"
              disabled={!camposValidos || salvando}
              onClick={() =>
                isEdit ? handleSalvarEdicao() : SalvarContribuicao()
              }
            >
              <label className="ml-2">
                {isEdit
                  ? "Salvar alteração"
                  : fila.length - 1 > 0
                  ? "Próximo"
                  : "Salvar"}
              </label>
            </AnttButton>
          </If>
        </div>
      </Card>

      <AjudaSigilo visible={showAjuda} onHide={() => setShowAjuda(false)} />

      <AjudaUpload
        visible={showAjudaUpload}
        onHide={() => setShowAjudaUpload(false)}
      />

      <ConfirmacaoSalvar
        visible={showSalvar}
        onHide={() => setShowSalvar(false)}
        onConfirm={confirmarSalvar}
        isUltimoItem={fila.length - 1 === 0}
      />

      <OverlaySalvamentoContribuicao
        visible={salvando}
        etapas={etapasSalvamento}
        etapaAtual={etapaAtual}
        dispositivoAtual={dispositivoAtual}
        totalDispositivos={fila.length}
        sucesso={sucesso}
        protocolo={protocoloSucesso}
        exibirAvisoEmail={exibirAvisoEmail}
        tituloSucesso={
          isEdit
            ? "Contribuição alterada com sucesso"
            : "Contribuição enviada com sucesso"
        }
        onClose={handleFecharOverlay}
      />

      <ModalContribuirComoEvento
        show={eventoFinalizadorController.showContribuirComo}
        handleFinalizar={() =>
          eventoFinalizadorController.confirmarContribuirComo()
        }
        onHide={() => {
          eventoFinalizadorController.onHideContribuirComo();
          controller.history.push(rotaVisualizar, {
            id: controller.location.state.codAudienciaPublica,
          });
        }}
        controller={eventoFinalizadorController}
      />
      <ModalCienciaEvento
        show={eventoFinalizadorController.showCiencia}
        handleFinalizar={(e: any) =>
          eventoFinalizadorController.finalizarCiencia(e)
        }
        onHide={() => eventoFinalizadorController.onHideCiencia()}
      />
      <ModalPesquisaSatisfacao
        show={eventoFinalizadorController.showSatisfacao}
        onHide={() => eventoFinalizadorController.onHideSatisfacao()}
        handleFinalizar={(e: any) => {
          eventoFinalizadorController.finalizarSatisfacao(e);
        }}
      />
    </>
  );
}
